import { apiUrl } from "../utils/api";
import { invalidateBrancheClassification, type BrancheLeaf } from "../data/brancheClassification";

/**
 * Pflege des Branchen-Katalogs (`/contact-branche-classification`) für den
 * Einstellungsbereich. Lesen darf jeder, schreiben nur `admin` — das Backend
 * setzt das durch, hier wird der 403 nur in eine verständliche Meldung
 * übersetzt.
 *
 * Nach jeder Änderung fliegt der modulweite Katalog-Cache weg: Anlage und Akte
 * zeigten sonst bis zum nächsten Reload den alten Stand.
 */

export interface KatalogVerwendung {
  id: number;
  hauptbranche: number;
  weitere: number;
  firmen: number;
  imPapierkorb: number;
}

export interface KatalogLoeschErgebnis {
  geloescht: boolean;
  deaktiviert: boolean;
  verwendung: KatalogVerwendung;
}

export interface KatalogUmbenennErgebnis {
  betroffen: number;
  firmenAktualisiert: number;
}

const BASIS = "contact-branche-classification";

/**
 * Fehlertext aus der Antwort ziehen. Nest liefert bei 409/400 ein
 * `{ message }` — genau der Satz, den der Nutzer lesen soll („„SaaS" gibt es in
 * „IT / Software" bereits."). Ohne ihn stünde da nur eine Statusnummer.
 */
const fehler = async (res: Response, fallback: string): Promise<Error> => {
  if (res.status === 403) {
    return new Error("Dafür fehlt die Berechtigung — den Katalog pflegen nur Administratoren.");
  }
  try {
    const body = await res.json();
    const msg = Array.isArray(body?.message) ? body.message.join(", ") : body?.message;
    if (typeof msg === "string" && msg.trim()) return new Error(msg);
  } catch {
    // Kein JSON-Body — dann bleibt es beim Standardtext.
  }
  return new Error(`${fallback} (HTTP ${res.status})`);
};

// Bewusst rohes fetch mit Content-Type: apiFetch setzt ihn nicht, der
// JSON-Body käme leer an (siehe brancheAssignmentService).
const schreiben = async <T>(
  pfad: string,
  method: "POST" | "PUT" | "DELETE",
  body: unknown,
  fallback: string,
): Promise<T> => {
  const res = await fetch(apiUrl(pfad), {
    method,
    credentials: "include",
    ...(body === undefined ? {} : { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  });
  if (!res.ok) throw await fehler(res, fallback);
  invalidateBrancheClassification();
  return res.json();
};

/** Ganzer Katalog inklusive der deaktivierten Einträge (die Pflege zeigt beide). */
export const katalogLaden = async (): Promise<BrancheLeaf[]> => {
  const res = await fetch(apiUrl(BASIS), { credentials: "include" });
  if (!res.ok) throw await fehler(res, "Katalog laden fehlgeschlagen");
  const d = await res.json();
  return Array.isArray(d) ? d : [];
};

/**
 * Neues Ebene-3-Blatt. Ist `ebene2` bzw. `ebene1` neu, entsteht damit zugleich
 * die Gruppe bzw. die Branche — im Datenmodell existieren sie nur über ihre
 * Blätter, eine neue Gruppe braucht deshalb zwingend ihre erste
 * Spezialisierung.
 */
export const blattAnlegen = (data: {
  ebene1: string;
  ebene2: string;
  ebene3: string;
  erlaeuterung?: string | null;
}): Promise<BrancheLeaf> => schreiben(BASIS, "POST", data, "Anlegen fehlgeschlagen");

/** Bezeichnung, Erläuterung oder Aktiv-Kennzeichen EINES Blattes ändern. */
export const blattAendern = (
  id: number,
  data: { ebene3?: string; erlaeuterung?: string | null; active?: boolean },
): Promise<BrancheLeaf> => schreiben(`${BASIS}/${id}`, "PUT", data, "Speichern fehlgeschlagen");

/** Branchengruppe umbenennen — wirkt auf ALLE ihre Blätter. */
export const gruppeUmbenennen = (
  ebene1: string,
  ebene2: string,
  neu: string,
): Promise<KatalogUmbenennErgebnis> =>
  schreiben(`${BASIS}/gruppe/umbenennen`, "PUT", { ebene1, ebene2, neu }, "Umbenennen fehlgeschlagen");

/** Branche umbenennen — wirkt auf ALLE Blätter aller ihrer Gruppen. */
export const brancheUmbenennen = (
  ebene1: string,
  neu: string,
): Promise<KatalogUmbenennErgebnis> =>
  schreiben(`${BASIS}/branche/umbenennen`, "PUT", { ebene1, neu }, "Umbenennen fehlgeschlagen");

/** Wie viele Firmen hängen an dem Blatt (Grundlage der Löschabfrage). */
export const verwendungLaden = async (id: number): Promise<KatalogVerwendung> => {
  const res = await fetch(apiUrl(`${BASIS}/${id}/verwendung`), { credentials: "include" });
  if (!res.ok) throw await fehler(res, "Verwendung ermitteln fehlgeschlagen");
  return res.json();
};

/** Löscht hart — oder deaktiviert nur, wenn Firmen den Eintrag tragen. */
export const blattLoeschen = (id: number): Promise<KatalogLoeschErgebnis> =>
  schreiben(`${BASIS}/${id}`, "DELETE", undefined, "Löschen fehlgeschlagen");
